/**
 * Commentary pacing.
 *
 * The engine already paces live commentary on the wire (a goal arrives as four
 * messages roughly 1-1.5s apart). The client only needs to smooth bursts that
 * land in a single tick, which happens on reconnect, on resync, and whenever
 * the socket batches frames.
 *
 * Rules:
 *  - saved / refetched history renders instantly, never dripped
 *  - live rows get a small stagger so they animate in one at a time
 *  - the whole backlog must be caught up inside MAX_BACKLOG_CATCHUP_MS
 */

export const BASE_STAGGER_MS = 350;
export const GOAL_OUTCOME_STAGGER_MS = 450;
export const MAX_BACKLOG_CATCHUP_MS = 2000;

// Past this many queued rows we are replaying history, not watching drama:
// reveal the whole backlog in one paint instead of animating hundreds of rows.
export const BURST_FLUSH_THRESHOLD = 25;

export const shouldFlushCommentaryBurstImmediately = (queuedCount) => (
    Number(queuedCount) > BURST_FLUSH_THRESHOLD
);

const isGoalOutcomeEvent = (event) => {
    const eventType = event?.event_type || event?.type || '';
    return event?.sub_type === 'outcome' && String(eventType).includes('GOAL');
};

/**
 * @param {object} event event that was just revealed
 * @param {number} queuedCount rows still waiting behind it
 * @returns {number} milliseconds to wait before revealing the next row
 */
export const getCommentaryDripDelayMs = (event, queuedCount = 0) => {
    if (!event || event._isHistory === true) return 0;

    const remaining = Number(queuedCount);
    if (!Number.isFinite(remaining) || remaining <= 0) return 0;

    const baseDelay = isGoalOutcomeEvent(event) ? GOAL_OUTCOME_STAGGER_MS : BASE_STAGGER_MS;
    const cappedDelay = Math.min(baseDelay, MAX_BACKLOG_CATCHUP_MS / remaining);

    return Math.max(0, Math.round(cappedDelay));
};

export default {
    getCommentaryDripDelayMs,
    shouldFlushCommentaryBurstImmediately,
    BASE_STAGGER_MS,
    GOAL_OUTCOME_STAGGER_MS,
    MAX_BACKLOG_CATCHUP_MS,
    BURST_FLUSH_THRESHOLD
};
